import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CornerDownLeftIcon, MessageSquareOffIcon } from 'lucide-react';
import { activities, contact } from '../../data/activities';
import { buildSmsThread, countThreadMessages } from '../../utils/conversation';
import { MessageBubble } from './MessageBubble';
interface SmsThreadProps {
  /** The timeline entry whose messages should be scrolled to and highlighted. */
  focusActivityId: string | null;
}
export function SmsThread({
  focusActivityId
}: SmsThreadProps) {
  const days = useMemo(() => buildSmsThread(activities), []);
  const total = useMemo(() => countThreadMessages(days), [days]);
  const firstName = contact.name.split(' ')[0];
  const containerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const hasScrolledOnce = useRef(false);
  const [focusOffscreen, setFocusOffscreen] = useState(false);
  const focusMessageId = useMemo(() => {
    if (!focusActivityId) return null;
    for (const day of days) {
      const match = day.messages.find(m => m.activityId === focusActivityId);
      if (match) return match.id;
    }
    return null;
  }, [days, focusActivityId]);
  const scrollToFocus = useCallback((smooth: boolean) => {
    const container = containerRef.current;
    const target = focusMessageId ? messageRefs.current[focusMessageId] : null;
    if (!container) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!target) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth && !reduce ? 'smooth' : 'auto'
      });
      return;
    }
    const top = target.offsetTop - container.clientHeight / 2 + target.offsetHeight / 2;
    container.scrollTo({
      top: Math.max(top, 0),
      behavior: smooth && !reduce ? 'smooth' : 'auto'
    });
  }, [focusMessageId]);
  useEffect(() => {
    scrollToFocus(hasScrolledOnce.current);
    hasScrolledOnce.current = true;
    setFocusOffscreen(false);
  }, [scrollToFocus]);
  const handleScroll = () => {
    const container = containerRef.current;
    const target = focusMessageId ? messageRefs.current[focusMessageId] : null;
    if (!container || !target) {
      setFocusOffscreen(false);
      return;
    }
    const top = target.offsetTop;
    const bottom = top + target.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;
    setFocusOffscreen(bottom < viewTop + 8 || top > viewBottom - 8);
  };
  if (total === 0) {
    return <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 bg-surface-muted px-8 text-center" data-id="element-57" data-mp="3fw216ixavlma">
        <MessageSquareOffIcon className="h-5 w-5 text-ink-subtle" aria-hidden="true" data-id="element-58" data-mp="31w0yqz1e064p" />
        <p className="font-display text-[13.5px] font-bold text-ink" data-id="element-59" data-mp="36uc9dbziaknz">
          No messages in this conversation
        </p>
        <p className="max-w-[240px] text-[12.5px] leading-relaxed text-ink-muted" data-id="element-60" data-mp="2n9u76r35tvm2">
          Nothing has been texted to or from {contact.phone} yet. Messages
          appear here as soon as one is sent or received.
        </p>
      </div>;
  }
  return <div className="relative flex min-h-0 flex-1 flex-col" data-id="element-61" data-mp="2s8f70pei2d9z">
      <div className="flex items-baseline justify-between gap-2 border-b border-line px-5 py-2.5" data-id="element-62" data-mp="2sb1d0klqskq3">
        <h4 className="font-display text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted" data-id="element-63" data-mp="3663ij6mog82d">
          Full conversation
        </h4>
        <span className="text-[11.5px] text-ink-subtle" data-id="element-64" data-mp="3fx7v5zovrgu1">
          {total} messages · {contact.phone}
        </span>
      </div>

      <div ref={containerRef} onScroll={handleScroll} className="timeline-scroll relative flex-1 overflow-y-auto bg-surface-muted px-5 py-4" data-id="element-65" data-mp="3azy9mv4m3pbh">
        {days.map(day => <section key={day.key} className="pb-1" data-id="element-66" data-mp="3kz6zbbsg2hs1">
            <div className="sticky top-0 z-10 -mx-1 mb-3 flex justify-center" data-id="element-67" data-mp="1ib6yl0nexwd5">
              <span className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-[11px] font-medium text-ink-muted" data-id="element-68" data-mp="1n9ib5j53ekz2">
                {day.label}
              </span>
            </div>
            <ul className="space-y-3 pb-4" data-id="element-69" data-mp="1wzlbk4ejn17f">
              {day.messages.map(message => <li key={message.id} ref={node => {
            messageRefs.current[message.id] = node;
          }} data-id="element-70" data-mp="zbt32ea1cdkj">
                  <MessageBubble message={message} focused={message.activityId === focusActivityId} contactFirstName={firstName} data-id="element-71" data-mp="14cqw5lwl2s0t" />
                </li>)}
            </ul>
          </section>)}
      </div>

      <AnimatePresence data-id="element-72" data-mp="2kriboy8ms4sp">
        {focusOffscreen && <motion.button type="button" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 6
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => scrollToFocus(true)} className="absolute bottom-3 left-1/2 z-20 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-line-strong bg-surface px-3 py-1.5 font-display text-[11.5px] font-bold text-ink shadow-panel" data-id="element-73" data-mp="2opg387pvj2z9">
            <CornerDownLeftIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-74" data-mp="3qg7jrul9bgzg" />
            Back to selected message
          </motion.button>}
      </AnimatePresence>
    </div>;
}